import { motion } from "framer-motion";
import { Bell, FileText, BookOpen, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, TELEGRAM_COMMUNITY_URL } from "@/config";

const LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_research-precision-1/artifacts/0d2556841c9f64b9_logo%20lighter.jpeg";

const TelegramIcon = ({ className = "h-7 w-7" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
  </svg>
);

const PERKS = [
  { icon: Bell, title: "New arrival alerts", desc: "Be first to know when fresh research resources land." },
  { icon: FileText, title: "Documentation updates", desc: "Specification and batch updates as they're published." },
  { icon: BookOpen, title: "Best-practice guides", desc: "Practical lab technique and handling resources." },
  { icon: Users, title: "A UK research network", desc: "Connect with like-minded researchers and educators." },
];

export const Community = () => {
  return (
    <section id="community" className="relative overflow-hidden py-24 bg-slate-50">
      {/* decorative gradient blobs */}
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-blue-300/30 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-sky-300/30 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-blue-600/10 px-3 py-1 font-mono2 text-xs uppercase tracking-[0.25em] text-blue-700">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-600" /> Community
            </span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Join the <span className="font-wordmark text-blue-600">{BRAND.name}</span> community.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              Connect with UK researchers, educators and laboratories on our Telegram group. Get documentation updates, new-arrival announcements and practical resources — direct from the team.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {PERKS.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="group flex items-start gap-3 rounded-xl bg-white p-4 ring-1 ring-slate-100 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:ring-blue-200"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white"><p.icon className="h-5 w-5" /></span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{p.title}</p>
                    <p className="text-xs leading-relaxed text-slate-500">{p.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0a1f44] via-[#0d47a1] to-[#1a73e8] p-8 text-center shadow-[0_40px_90px_-30px_rgba(13,71,161,0.7)] sm:p-10">
              {/* glow + grid texture */}
              <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 50% 0%, rgba(96,165,250,0.5), transparent 60%)" }} />
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-sky-400/30 blur-2xl" />

              {/* collab logos */}
              <div className="relative flex items-center justify-center gap-5">
                <span className="h-16 w-16 overflow-hidden rounded-full ring-2 ring-white/80 shadow-lg">
                  <img src={LOGO} alt="PepsWorld UK" className="h-full w-full scale-[1.18] object-cover" />
                </span>
                <span className="font-display text-xl font-light text-white/60">×</span>
                <span className="relative grid h-16 w-16 place-items-center">
                  <span className="pulse-ring absolute inset-0 rounded-full bg-sky-300/50" />
                  <span className="pulse-ring absolute inset-0 rounded-full bg-sky-300/40" style={{ animationDelay: "1.4s" }} />
                  <span className="relative grid h-16 w-16 place-items-center rounded-full bg-[#229ED9] text-white shadow-lg ring-2 ring-white/80">
                    <TelegramIcon className="h-8 w-8" />
                  </span>
                </span>
              </div>

              <p className="relative mt-5 font-mono2 text-[11px] uppercase tracking-[0.35em] text-sky-200">PepsWorld UK <span className="text-white/50">×</span> Telegram</p>
              <h3 className="relative mt-2 font-display text-3xl font-bold text-white">Stay in the loop</h3>
              <p className="relative mx-auto mt-3 max-w-sm text-sm leading-relaxed text-blue-100/90">Hundreds of UK researchers share updates, batch info and support. Free to join — tap below to come in.</p>

              {/* member row */}
              <div className="relative mt-6 flex items-center justify-center gap-3">
                <div className="flex -space-x-2">
                  {["#60a5fa", "#38bdf8", "#818cf8", "#22d3ee"].map((c, i) => (
                    <span key={i} className="h-7 w-7 rounded-full border-2 border-[#0d47a1]" style={{ background: c }} />
                  ))}
                </div>
                <span className="text-xs font-medium text-blue-100">500+ researchers inside</span>
              </div>

              <a href={TELEGRAM_COMMUNITY_URL} target="_blank" rel="noreferrer" data-testid="community-telegram" className="relative mt-7 block">
                <Button className="h-12 w-full bg-white font-semibold text-[#0d47a1] shadow-lg transition-transform hover:scale-[1.02] hover:bg-sky-50">
                  <TelegramIcon className="mr-2 h-5 w-5" /> Join the Telegram group
                </Button>
              </a>
              <p className="relative mt-4 font-mono2 text-xs text-blue-200/80">@pepsworlduk · t.me/pepsworlduk</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Community;
